import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { Map, List } from 'immutable'
import { compose } from 'recompose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../actions/index'
import {
  TYPE_CONTENT_UNKNOWN,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_ARTICLE,
} from '../../../services/content-type/index'
import { EN } from '../../../services/languages/constants'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { selectUserLanguages } from '../../../services/user/index'
import { getPrimary } from '../../../services/languages/index'
import TileV2Placeholder from '../Tile.v2Placeholder'
import TileV2GraphqlHero from './Tile.v2GraphqlHero'
import TileV2Info from '../Tile.v2Info'
import TileV2GraphqlInfoMetadata from './Tile.v2GraphqlInfoMetadata'
import TileV2GraphqlBottomMetadata from './Tile.v2GraphqlBottomMetadata'
import {
  TILE_V2_DISPLAY_DEFAULT,
  TILE_V2_DISPLAY_VERTICAL,
  TILE_V2_DISPLAY_ROUND,
  TILE_V2_DISPLAY_POSTER,
} from '../constants'
import {
  getTileV2ClassName,
} from '../utils'

/**
 * Tile v2 Graphql component
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.disablePlaceholder - Whether or not to show the loading placeholder
 * @param {JSX.Element|null} props.editComponent - The edit component from playlists
 * @param {String} props.display - The display type
 * @param {Bookean} props.touchable - Whether or not the device is touch capable
 * @param {Bookean} props.hideLabels - Whether or not labels are hidden
 * @param {Bookean} props.hideTags - Whether or not tags are hidden
 *  or watch history
 * @returns {JSX.Element} The tile component
 */
function TileV2Graphql (props) {
  const {
    data,
    contentType,
    disablePlaceholder,
    editComponent,
    display,
    touchable,
    showNumbers,
    showStack,
    index,
  } = props
  const id = data.get('contentId')

  const noData = !data || !Map.isMap(data) || !data.size || !id || id <= 0
  const [isTouchable, setIsTouchable] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [mouseIn, setMouseIn] = useState(false)
  const hoverDelay = 450
  const handleMouseEnter = () => {
    setMouseIn(true)
  }
  const handleMouseLeave = () => {
    setMouseIn(false)
  }

  // Using local state to fix an invariant violation on the share page
  // all other pages are fine without this fix
  useEffect(() => {
    setIsTouchable(touchable)
  }, [touchable])

  // Setting and clearing the hover timeout
  useEffect(() => {
    let hoverTimeout

    if (mouseIn) {
      hoverTimeout = setTimeout(() => {
        setIsHovered(true)
      }, hoverDelay)
    } else {
      setIsHovered(false)
    }

    return () => {
      clearTimeout(hoverTimeout)
    }
  }, [mouseIn])

  if (disablePlaceholder && noData) {
    return null
  }

  if (noData) {
    return <TileV2Placeholder display={display} />
  }

  if (showStack) {
    return (
      <div className={getTileV2ClassName(props, false)}>
        <div className="tile-v2__wrapper">
          <div className="tile-v2__stack-cell">
            <TileV2GraphqlHero {...props} hovered={false} isTouchable={isTouchable} />
            <div className="tile-v2__stack">
              <div className="tile-v2__stack-item"></div>
              <div className="tile-v2__stack-item"></div>
              <div className="tile-v2__stack-item"></div>
            </div>
          </div>
          <TileV2GraphqlInfoMetadata {...props} hovered={false} showStack={true} />
        </div>
      </div>
    )
  }

  if (showNumbers) {
    return (
      <div className={getTileV2ClassName(props, isHovered)}>
        <div className={`tile-v2__wrapper-grid tile-v2__wrapper-grid--${display}`}>
          <div className={`tile-v2__wrapper-cell tile-v2__wrapper-cell--${index + 1}`}>{' '}</div>
          <div className="tile-v2__wrapper-cell">
            <div
              className="tile-v2__wrapper"
              onMouseEnter={!isTouchable ? handleMouseEnter : null}
              onMouseLeave={!isTouchable ? handleMouseLeave : null}
            >
              <TileV2GraphqlHero {...props} hovered={isHovered} isTouchable={isTouchable} />
              <TileV2Info
                {...props}
                hovered={editComponent || contentType === TYPE_CONTENT_PRODUCT || contentType === 'article' ? false : isHovered}
                isTouchable={isTouchable}
                useGraphQL
              />
              {
                contentType === TYPE_CONTENT_PERSON
                  ? <TileV2GraphqlInfoMetadata {...props} hovered={isHovered} />
                  : null
              }
            </div>
          </div>
          <div className="tile-v2__wrapper-cell">{' '}</div>
          <div className="tile-v2__wrapper-cell">
            {
              contentType === TYPE_CONTENT_TAG
              || contentType === TYPE_CONTENT_PRODUCT
              || contentType === TYPE_CONTENT_ARTICLE // PoC: HubsV2
                ? <TileV2GraphqlInfoMetadata
                    {...props}
                    hovered={contentType === TYPE_CONTENT_PRODUCT ? false : isHovered}
                  />
                : <TileV2GraphqlBottomMetadata {...props} isTouchable={isTouchable} />
            }
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={getTileV2ClassName(props, isHovered)}>
      {editComponent || null}
      <div
        className="tile-v2__wrapper"
        onMouseEnter={!isTouchable ? handleMouseEnter : null}
        onMouseLeave={!isTouchable ? handleMouseLeave : null}
      >
        <TileV2GraphqlHero {...props} hovered={isHovered} isTouchable={isTouchable} />
        <TileV2Info
          {...props}
          hovered={editComponent || contentType === TYPE_CONTENT_PRODUCT ? false : isHovered}
          isTouchable={isTouchable}
          useGraphQL
        />
        {
          contentType === TYPE_CONTENT_PERSON
            ? <TileV2GraphqlInfoMetadata {...props} hovered={isHovered} />
            : null
        }
      </div>
      {
        contentType === TYPE_CONTENT_TAG
        || contentType === TYPE_CONTENT_PRODUCT
        || contentType === TYPE_CONTENT_ARTICLE
          ? <TileV2GraphqlInfoMetadata
              {...props}
              hovered={contentType === TYPE_CONTENT_PRODUCT ? false : isHovered}
            />
          : <TileV2GraphqlBottomMetadata {...props} isTouchable={isTouchable} />
      }
    </div>
  )
}

TileV2Graphql.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  upstreamContext: ImmutablePropTypes.map,
  resolver: ImmutablePropTypes.map.isRequired,
  subscriptions: ImmutablePropTypes.list.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  contentType: PropTypes.string.isRequired,
  contentSubtype: PropTypes.string,
  touchable: PropTypes.bool.isRequired,
  sectionIndex: PropTypes.number,
  rowPlacement: PropTypes.number,
  adminTitle: PropTypes.string,
  index: PropTypes.number,
  display: PropTypes.oneOf([
    TILE_V2_DISPLAY_DEFAULT,
    TILE_V2_DISPLAY_VERTICAL,
    TILE_V2_DISPLAY_ROUND,
    TILE_V2_DISPLAY_POSTER,
  ]),
  editComponent: PropTypes.element,
  disablePlaceholder: PropTypes.bool,
  pageLanguage: PropTypes.string,
  onClick: PropTypes.func,
  setEventDefault: PropTypes.func.isRequired,
  setUpstreamContext: PropTypes.func.isRequired,
  imageOptimization: PropTypes.bool,
  hideLabels: PropTypes.bool,
  hideTags: PropTypes.bool,
  showNumbers: PropTypes.bool,
  showStack: PropTypes.bool,
  section: ImmutablePropTypes.map,
  onRemove: PropTypes.func,
  isAnimating: PropTypes.bool,
  isContinueWatchingV3: PropTypes.bool,
  sendOptimizelyFeatureEvent: PropTypes.func.isRequired,
}

TileV2Graphql.defaultProps = {
  data: Map(),
  upstreamContext: Map(),
  subscriptions: List(),
  display: TILE_V2_DISPLAY_DEFAULT,
  disablePlaceholder: false,
  editComponent: null,
  imageOptimization: true,
  hideLabels: false,
  hideTags: false,
  section: Map(),
}

export default compose(
  connectRedux(
    (state, props) => {
      const { data } = props
      const isLoggedIn = getAuthIsLoggedIn(state.auth)
      const contentType = data.get('contentType', TYPE_CONTENT_UNKNOWN)
      const contentSubtype = data.get('contentSubtype')
      const userLanguages = selectUserLanguages(state)
      // Not sure how we will want to handle this for GraphQL
      // const editComponent =
      // data.get('playlistEditComponent', null) || data.get('hideWatchedEditComponent', null)
      const userLanguage = getPrimary(state.user.getIn(['data', 'language']))

      return {
        isLoggedIn,
        contentType,
        contentSubtype,
        staticText: state.staticText.getIn(['data', 'tileV2', 'data']),
        touchable: state.app.getIn(['viewport', 'touchable'], false),
        auth: state.auth,
        subscriptions: state.auth.get('subscriptions', List()),
        country: state.auth.get('country'),
        userLanguage,
        userLanguages,
        resolver: state.resolver,
        pageLanguage: state.page.get('lang', EN),
        viewportWidth: state.app.getIn(['viewport', 'width']),
        // editComponent,
        // This is used to signal the use of GraphQL data as opposed to the old data structures
        useGraphQLData: true,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        addToasty: actions.toasty.addToasty,
        setEventDefault: actions.eventTracking.setEventDefault,
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(TileV2Graphql)
